import React, { useEffect, useMemo, useState } from 'react'

import { Card } from '@wartek-id/card'
import type { OptionItem } from '@wartek-id/select'
import { Shimmer } from '@wartek-id/shimmer'

import { KinerjaHarianDeleteConfirmModal } from 'app/KinerjaHarian/components/logs/KinerjaHarianDeleteConfirmModal'
import { KinerjaHarianCalendarDetailPopup } from 'app/KinerjaHarian/components/logs/KinerjaHarianCalendarDetailPopup'
import { KinerjaHarianCalendarView } from 'app/KinerjaHarian/components/logs/KinerjaHarianCalendarView'
import { KinerjaHarianEmptyState } from 'app/KinerjaHarian/components/logs/KinerjaHarianEmptyState'
import { KinerjaHarianLogsHeader } from 'app/KinerjaHarian/components/logs/KinerjaHarianLogsHeader'
import { KinerjaHarianListItem } from 'app/KinerjaHarian/components/logs/KinerjaHarianListItem'
import { KinerjaHarianToolbar } from 'app/KinerjaHarian/components/logs/KinerjaHarianToolbar'
import { Pagination } from 'app/KinerjaShared/components/Pagination'
import {
  ALL_MONTH_OPTION,
  MONTHS,
  PAGE_SIZE,
  EDIT_DELETE_DISABLED_STATES,
} from 'app/KinerjaHarian/constants'
import { useKinerjaHarianTrackEvent } from 'app/KinerjaHarian/hooks/useKinerjaHarianTrackEvent'
import { formatDate } from 'utils/datetime'

import type { KinerjaHarianLog, KinerjaHarianView } from 'types/kinerja-harian'

function createMonthOption(monthIndex: number, year: number): OptionItem {
  const monthLabel = MONTHS[monthIndex]

  return {
    name: `${monthLabel} ${year}`,
    option: `${monthLabel.toLowerCase()}-${year}`,
  }
}

export function getMonthDateRange(selectedMonthOption: string, year: number) {
  if (selectedMonthOption === ALL_MONTH_OPTION) {
    return {
      startDate: `${year}-01-01`,
      endDate: `${year}-12-31`,
    }
  }

  const monthIndex = MONTHS.findIndex(
    (month) => `${month.toLowerCase()}-${year}` === selectedMonthOption
  )

  if (monthIndex < 0) {
    return {
      startDate: `${year}-01-01`,
      endDate: `${year}-12-31`,
    }
  }

  const startDate = new Date(year, monthIndex, 1)
  const endDate = new Date(year, monthIndex + 1, 0)
  const formatDate = (date: Date) => {
    const month = `${date.getMonth() + 1}`.padStart(2, '0')
    const day = `${date.getDate()}`.padStart(2, '0')

    return `${date.getFullYear()}-${month}-${day}`
  }

  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
  }
}

interface KinerjaHarianLogsProps {
  isLoading?: boolean
  harianLogs: KinerjaHarianLog[]
  selectedMonthOption: string
  selectedYear: number
  onCreate: () => void
  onDelete: (itemId: string) => void
  onEdit: (itemId: string) => void
  onMonthClick: () => void
  onMonthChange: (option: OptionItem) => void
  onViewChange: (view: KinerjaHarianView) => void
}

export function KinerjaHarianLogs({
  isLoading = false,
  harianLogs,
  selectedMonthOption,
  selectedYear,
  onCreate,
  onDelete,
  onEdit,
  onMonthClick,
  onMonthChange,
  onViewChange,
}: KinerjaHarianLogsProps) {
  const { trackEvent } = useKinerjaHarianTrackEvent()

  const [activeView, setActiveView] = useState<KinerjaHarianView>('list')
  const [currentPage, setCurrentPage] = useState(1)
  const [expandedItemIds, setExpandedItemIds] = useState<string[]>([])
  const [calendarSelectedItem, setCalendarSelectedItem] =
    useState<KinerjaHarianLog | null>(null)
  const [pendingDeleteEntryDate, setPendingDeleteEntryDate] = useState<
    string | null
  >(null)
  const monthOptions = useMemo<OptionItem[]>(
    () => [
      {
        name: 'Semua Bulan',
        option: ALL_MONTH_OPTION,
      },
      ...MONTHS.map((_, monthIndex) =>
        createMonthOption(monthIndex, selectedYear)
      ),
    ],
    [selectedYear]
  )
  const selectedMonth =
    monthOptions.find((option) => option.option === selectedMonthOption) ||
    monthOptions[0]
  const hasAvailableMonthFilters = monthOptions.length > 1

  const kinerjaKinerjaHarianLogs = useMemo<KinerjaHarianLog[]>(() => {
    return harianLogs
      .slice()
      .sort((currentItem, nextItem) =>
        currentItem.entryDate.localeCompare(nextItem.entryDate)
      )
  }, [harianLogs])

  useEffect(() => {
    setCurrentPage(1)
    setExpandedItemIds([])
  }, [selectedMonthOption])

  const totalPages = Math.max(
    1,
    Math.ceil(kinerjaKinerjaHarianLogs.length / PAGE_SIZE)
  )
  const clampedPage = Math.min(currentPage, totalPages)
  const startIndex = (clampedPage - 1) * PAGE_SIZE
  const paginatedItems = kinerjaKinerjaHarianLogs.slice(
    startIndex,
    startIndex + PAGE_SIZE
  )

  const handleToggleExpanded = (itemId: string) => {
    setExpandedItemIds((prev) =>
      prev.includes(itemId)
        ? prev.filter((currentId) => currentId !== itemId)
        : [...prev, itemId]
    )
  }
  const handleViewChange = (view: KinerjaHarianView) => {
    setActiveView(view)
    onViewChange(view)
  }
  const shouldShowToolbar =
    hasAvailableMonthFilters &&
    !(
      selectedMonthOption === ALL_MONTH_OPTION &&
      kinerjaKinerjaHarianLogs.length === 0
    )

  return (
    <Card>
      <KinerjaHarianLogsHeader onCreate={onCreate} />

      {isLoading ? (
        <div className="p-6 space-y-3">
          <Shimmer noOfLines={8} height={68} width="100%" />
        </div>
      ) : (
        <>
          <div className="p-6 space-y-6">
            {shouldShowToolbar && (
              <KinerjaHarianToolbar
                activeView={activeView}
                monthOptions={monthOptions}
                selectedMonth={selectedMonth}
                selectedYear={selectedYear}
                onMonthClick={onMonthClick}
                onMonthChange={onMonthChange}
                onViewChange={handleViewChange}
              />
            )}

            {activeView === 'list' && kinerjaKinerjaHarianLogs.length === 0 && (
              <KinerjaHarianEmptyState />
            )}

            {activeView === 'calendar' && shouldShowToolbar && (
              <KinerjaHarianCalendarView
                items={kinerjaKinerjaHarianLogs}
                selectedMonthOption={selectedMonthOption}
                onClick={(item) => {
                  if (item) {
                    setCalendarSelectedItem(item)
                    trackEvent({
                      activityType: 'click_choose_date',
                      screenName: 'harian',
                      additionalParams: {
                        clicked_text: formatDate(item.entryDate),
                      },
                    })
                  }
                }}
              />
            )}

            {activeView === 'list' && (
              <>
                <div className="space-y-3">
                  {paginatedItems.map((item) => {
                    return (
                      <KinerjaHarianListItem
                        key={item.entryDate}
                        isExpanded={expandedItemIds.includes(item.entryDate)}
                        item={item}
                        onDelete={(itemId: string) => {
                          setPendingDeleteEntryDate(itemId)
                          trackEvent({
                            activityType: 'click_edit',
                            screenName: 'harian',
                            additionalParams: {
                              edit_type: 'delete',
                            },
                          })
                        }}
                        onEdit={onEdit}
                        onToggleExpanded={handleToggleExpanded}
                      />
                    )
                  })}
                </div>
              </>
            )}
          </div>

          {activeView === 'list' &&
            kinerjaKinerjaHarianLogs.length > PAGE_SIZE && (
              <Pagination
                className="px-6 py-5 border-t border-gray-30"
                currentPage={clampedPage}
                itemLabel="data"
                pageSize={PAGE_SIZE}
                totalItems={kinerjaKinerjaHarianLogs.length}
                onPageChange={setCurrentPage}
              />
            )}
        </>
      )}

      <KinerjaHarianCalendarDetailPopup
        isOpen={calendarSelectedItem !== null}
        item={calendarSelectedItem}
        isEditDisabled={
          calendarSelectedItem !== null &&
          EDIT_DELETE_DISABLED_STATES.includes(calendarSelectedItem.state)
        }
        onClose={() => setCalendarSelectedItem(null)}
        onEdit={onEdit}
      />

      <KinerjaHarianDeleteConfirmModal
        isOpen={pendingDeleteEntryDate !== null}
        onClose={() => {
          setPendingDeleteEntryDate(null)
          trackEvent({
            activityType: 'click_popup_delete',
            screenName: 'harian',
            additionalParams: {
              clicked_text: 'Kembali',
            },
          })
        }}
        onConfirm={() => {
          onDelete(pendingDeleteEntryDate)
          setPendingDeleteEntryDate(null)
          trackEvent({
            activityType: 'click_popup_delete',
            screenName: 'harian',
            additionalParams: {
              clicked_text: 'Konfirmasi',
            },
          })
        }}
      />
    </Card>
  )
}
